import Immutable from 'immutable';
import { Subject, fromEvent, interval, merge } from 'rxjs';
import { map, scan, tap, filter, share } from 'rxjs/operators';

import { SlideTransition } from './transition';

const playbackButtonState = {
    PAUSE: 'pause',
    PLAY: 'play',
    STOP: 'stop',
    REPLAY: 'replay',
};

/**
 * @typedef {Object} GalleryProps
 * @property {number} index     Current index.
 * @property {boolean} loop     Should playback loop?
 * @property {number} interval  Duration until moving to next frame in milliseconds.
 */

/**
 * @typedef {Object} Transition
 * @property {function} onLoad
 *           Transition function called during construction.
 *           Receives contents as argument.
 * @property {function} onTransition
 *           Transition function called everytime index changes.
 *           Receives contents as argument.
 */

/**
 * @typedef {Object} Gallery
 * @property {Object} dom       DOM elements.
 * @property {function} next    Display next content.
 * @property {function} prev    Display previous content.
 * @property {function} play    Start playback.
 * @property {function} pause   Pause playback.
 * @property {function} stop    Pause playback and go to first content.
 * @property {function} replay  Go to first content and start playback.
 * @property {RxJS.Observable} gallery$
 *           Observable of Gallery's state stream. Can be subscribed to monitor state changes.
 * @property {RxJS.Observable} external$
 *           Observable of external events stream. Can be used to trigger state change.
 */

/**
 * Initialise Gallery features.
 *
 * @param {HTMLDomElement} element Gallery's root element.
 * @param [GalleryProps] props Gallery's properties ({number} index, {number} interval, {bool} loop).
 * @param [Transition] transition Gallery's transition.
 *
 * @return {Gallery}
 */
export default function Gallery(element, props = {}, transition = SlideTransition()) {
    const dom = queryComponents(element);

    const iState = Immutable.fromJS(Immutable.merge({
        index: 0,
        length: dom.contents.length,
        loop: true,
        defaultInterval: 4000,
        _timeBucket: Infinity,
    }, props));
    const initialState = iState.set('intervals', getIntervals(dom.contents, iState.get('defaultInterval')));

    const events$ = domObservable(dom);
    const interval$ = animationFrameObservable();
    const external$ = new Subject();

    // impurity ?
    let localState = initialState;

    const main$ = merge(events$, interval$, external$)
        .pipe(
            scan((state, changeFn) => changeFn(state), initialState),
            filter(state => !state.equals(localState)),
            tap(state => {
                localState = state;
            }),
            share(),
        );

    main$.subscribe(
        // onNext
        state => {
            render(dom, state, transition.onTransition);
        },
        // onError
        error => console.log(error), // eslint-disable-line no-console
        // onComplete
        () => {},
    );

    dom.root.classList.remove('no-js');
    dom.root.classList.add('news-vj-component-gallery--active');
    transition.onTransition(dom.contents, initialState.get('index'));

    window.setTimeout(() => {
        transition.onLoad(dom.contents);
    }, 0);

    return externalObservableHelper(external$, { dom, gallery$: main$ });
}

/**
 * Query required components of gallery.
 *
 * @param {HTMLDomElement} root Root element of the gallery.
 */
function queryComponents(root) {
    if (!root) {
        throw new Error('invalid HTML element node');
    }
    const contents = Array.from(root.querySelectorAll('.content'));
    const total = contents.length;

    const prevButton = root.querySelector('.icon-button--previous');
    const nextButton = root.querySelector('.icon-button--next');

    const playbackButton = root.querySelector('.icon-button--play');
    playbackButton.setAttribute('data-state', playbackButtonState.PLAY);

    const progressBar = root.parentNode.querySelector('.news-vj-component-gallery__progress');
    const { progressHighlight, progressButtons } = queryProgressbarComponents(progressBar, total);

    return {
        root,
        contents,
        prevButton,
        nextButton,
        playbackButton,
        progressBar,
        progressHighlight,
        progressButtons,
    };
}

/**
 * Query required components of gallery's progress bar.
 *
 * @param {HTMLDomElement} progressBar Progress bar element.
 * @param {number} length How many contents in the gallery.
 */
function queryProgressbarComponents(progressBar, length) {
    if (!progressBar) {
        return {};
    }
    const progressHighlight = progressBar.querySelector('.progress__highlight');

    const offscreenProgressContainer = progressBar.querySelector('.offscreen-progress__container');
    const progressButtons = Array.from(Array(length).keys()).map(i => {
        const offscreenProgressButton = document.createElement('div');
        offscreenProgressButton.classList.add('offscreen-progress__button');
        offscreenProgressButton.setAttribute('data-index', i);
        offscreenProgressContainer.appendChild(offscreenProgressButton);
        return offscreenProgressButton;
    });

    return {
        progressHighlight,
        progressButtons,
    };
}

/**
 * Make an Observable of DOM events stream in gallery.
 * All events are transformed into this form:
 *      (state) => transformState(state)
 *
 * @param {Object} dom DOM elements of the gallery, queried through `queryComponents`.
 */
function domObservable(dom) {
    return merge(
        fromEvent(dom.prevButton, 'click')
            .pipe(map(() => state => stateHelper(state).prev())),
        fromEvent(dom.nextButton, 'click')
            .pipe(map(() => state => stateHelper(state).next())),
        fromEvent(dom.playbackButton, 'click')
            .pipe(
                map(ev => ev.target.getAttribute('data-state')),
                map(buttonState => {
                    switch (buttonState) {
                    case playbackButtonState.PAUSE:
                        return state => stateHelper(state).pause();
                    case playbackButtonState.PLAY:
                        return state => stateHelper(state).play();
                    case playbackButtonState.STOP:
                        return state => stateHelper(state).stop();
                    case playbackButtonState.REPLAY:
                    default:
                        return state => stateHelper(state).replay();
                    }
                }),
            ),
    );
}

/**
 * Make an Observable of a constant interval stream.
 * Will increment state's index after some interval.
 * All events are transformed into this form:
 *      (state) => transformState(state)
 */
function animationFrameObservable() {
    return interval(100)
        .pipe(
            map(() => 100),
            map(delta => state => {
                const nstate = state.set('_timeBucket', state.get('_timeBucket') - delta);
                if (nstate.get('_timeBucket') > 0) {
                    return nstate;
                }
                return fillTimeBucket(normaliseIndex(nstate.set('index', nstate.get('index') + 1)));
            }),
        );
}

/**
 * Convenient functions to transform given state.
 *
 * @param {Immutable.Map} state Gallery's state.
 */
export function stateHelper(state) {
    return {
        state,
        atBegin() {
            return state.get('index') === 0;
        },
        atEnd() {
            return state.get('index') === state.get('length') - 1;
        },
        isLooping() {
            return state.get('loop');
        },
        isPlaying() {
            return state.get('_timeBucket') !== Infinity;
        },
        diff(prevState) {
            return state.filter((value, key) => prevState.get(key) !== value);
        },
        next() {
            return normaliseIndex(state
                .set('_timeBucket', Infinity)
                .set('index', state.get('index') + 1));
        },
        prev() {
            return normaliseIndex(state
                .set('_timeBucket', Infinity)
                .set('index', state.get('index') - 1));
        },
        play() {
            return state
                .set('_timeBucket', state.get('intervals')[state.get('index')]);
        },
        pause() {
            return state
                .set('_timeBucket', Infinity);
        },
        stop() {
            return state
                .set('_timeBucket', Infinity)
                .set('index', 0);
        },
        replay() {
            return state
                .set('_timeBucket', state.get('intervals')[0])
                .set('index', 0);
        },
        normaliseIndex() {
            return normaliseIndex(state);
        },
        fillTimeBucket() {
            return fillTimeBucket(state);
        },
    };
}

function normaliseIndex(state) {
    const s = stateHelper(state);
    const niFn = s.isLooping() ? ringIndex : clampIndex;
    return state.set('index', niFn(state.get('index'), state.get('length')));
}

function fillTimeBucket(state) {
    return state.set('_timeBucket', state.get('intervals')[state.get('index')]);
}

/**
 * Convenient functions to trigger an event to given Subject.
 * Used to transform Gallery state from library user space.
 *
 * @param {RxJS.Subject} subject Gallery's subject.
 */
function externalObservableHelper(subject, others) {
    return {
        ...others,
        external$: subject,
        next() {
            return subject.next(state => stateHelper(state).next());
        },
        prev() {
            return subject.next(state => stateHelper(state).prev());
        },
        play() {
            return subject.next(state => stateHelper(state).play());
        },
        pause() {
            return subject.next(state => stateHelper(state).pause());
        },
        stop() {
            return subject.next(state => stateHelper(state).stop());
        },
        replay() {
            return subject.next(state => stateHelper(state).replay());
        },
    };
}

/**
 * Alter the DOM based on given state.
 *
 * @param {Object} dom Gallery's DOM elements queried through `queryComponents`.
 * @param {Immutable.Map} state Gallery's state.
 * @param {function} transition Function that will be triggered when index has been changed.
 */
function render(dom, state, transition) {
    const {
        atBegin, atEnd, isLooping, isPlaying,
    } = stateHelper(state);
    const index = state.get('index');
    dom.root.setAttribute('data-index', index);

    toggleAttribute(dom.prevButton, 'disabled', atBegin() && !isLooping());
    toggleAttribute(dom.nextButton, 'disabled', atEnd() && !isLooping());
    if (isPlaying()) {
        setPlaybackState(dom.playbackButton, playbackButtonState.PAUSE);
    }
    else if (!isPlaying() && atEnd()) {
        setPlaybackState(dom.playbackButton, playbackButtonState.REPLAY);
    }
    else {
        setPlaybackState(dom.playbackButton, playbackButtonState.PLAY);
    }

    if (isPlaying()) {
        dom.root.classList.add('news-vj-component-gallery--playing');
    }
    else {
        dom.root.classList.remove('news-vj-component-gallery--playing');
    }

    const progressTransform = `translate(${index * 100}%)`;
    vendorTransform.bind(dom.progressHighlight)(progressTransform);

    transition(dom.contents, index);
}

/**
 * Set attribute to playbcak button according to given state.
 *
 * @param {HTMLDomElement} button - playback button DOM element
 * @param {playbackButtonState} state
 */
function setPlaybackState(button, state) {
    const buttonStateVocabValue = __(`vocab-list_1_${state}`);
    button.setAttribute('class', `icon-button icon-button--${state}`);
    button.setAttribute('data-state', state);
    button.setAttribute('aria-label', buttonStateVocabValue);
    button.setAttribute('title', buttonStateVocabValue);
}

/**
 * Get index of an element in a list of given length as if the container is a circular buffer.
 * Example:
 *      const foo = ['a', 'b', 'c', 'd'];
 *      console.log(foo[ringIndex(3, foo.length)]);  // 'd'
 *      console.log(foo[ringIndex(4, foo.length)]);  // 'a'
 *      console.log(foo[ringIndex(-1, foo.length)]); // 'd'
 *      console.log(foo[ringIndex(-4001, foo.length)]); // Error!
 *
 * @param {number} index Element index, must not be smaller than -1000 * length.
 * @param {number} length Container length.
 * @return {number} Actual index of the element.
 */
function ringIndex(index, length) {
    const ni = 1000 * length + index;
    if (ni < length) {
        return ni;
    }
    return ni % length;
}

/**
 * Clamp a given index based on given length.
 * Example:
 *      const foo = ['a', 'b', 'c', 'd'];
 *      console.log(foo[clampIndex(3, foo.length)]);  // 'd'
 *      console.log(foo[clampIndex(4, foo.length)]);  // 'd'
 *      console.log(foo[clampIndex(-1, foo.length)]); // 'a'
 *
 * @param {number} index Element index.
 * @param {number} length Container length.
 * @return {number} Actual index of the element.
 */
function clampIndex(index, length) {
    return Math.max(0, Math.min(length - 1, index));
}

function getIntervals(contents, defaultInterval) {
    return contents
        .map(c => c.getAttribute('data-duration') || defaultInterval);
}

function toggleAttribute(element, attr, condition) {
    if (condition) {
        element.setAttribute(attr, true);
    }
    else {
        element.removeAttribute(attr);
    }
}

function vendorTransform(transform) {
    this.style.WebkitTransform = transform;
    this.style.msTransform = transform;
    this.style.transform = transform;
}
